import { remoteComponentSubscribe } from "components/remote-trigger/browser";
import { initComponent } from "../../utilities/common";
import { postAnalytics } from "../../utilities/analytics";
import { parseAllowedChatUrl } from "../../utilities/chat-config";

const getChatOrigin = () =>
  parseAllowedChatUrl(window.PUBLIC_ENV?.CHAT_ORIGIN)?.origin ?? "";

const getIframeOrigin = (iframe) => {
  const src = iframe?.getAttribute("src");

  if (!src) {
    return "";
  }

  try {
    return new URL(src, window.location.href).origin;
  } catch (error) {
    return "";
  }
};

export default class Chat {
  constructor(element) {
    this.injected = false;
    this.element = element;
    this.chatIframeWrapper = element.querySelector("[chatIframeWrapper]");
    this.churchChatLoaded = false;
    const chatIframeWrapper = this.chatIframeWrapper;
    const closeButton = chatIframeWrapper.querySelector("button");
    // get the PCTA FAB if it exists
    const pcta = document.querySelector("[data-type='persistent-cta'] [extra]");
    if (pcta) {
      // move chat into the PCTA so the FAB can react to it on mobile
      pcta.appendChild(element);
    }
    // Check if enhanced PCTA exists and add a data attribute for styling
    const enhancedPcta = document.querySelector(
      "[data-type='enhanced-persistent-cta']"
    );
    if (enhancedPcta) {
      element.setAttribute("data-near-enhanced-pcta", "");
    }
    const fab = element.querySelector("[data-type='floating-action-button']");
    const docElem = document.documentElement;
    const showChat = document.querySelector("body[showchat]");

    // hack for ios 100vh bug https://dev.to/maciejtrzcinski/100vh-problem-with-ios-safari-3ge9
    const appHeight = () => {
      docElem.style.setProperty("--app-height", `${window.innerHeight}px`);
      // in case they open chat and reseize browser after that ... for some reason
      if (!sessionStorage.getItem("chatOpen") && !fab) {
        fab.show();
      }
      if (window.innerHeight >= 600) {
        fab.hide();
      }
      if (
        (window.innerWidth <= 599 || window.innerHeight <= 599) &&
        sessionStorage.getItem("chatOpen")
      ) {
        chatIframeWrapper.style.bottom = 0;
      }
      // Extra check for slow repaints that affect the chat position sometimes
      setTimeout(() => {
        if (chatIframeWrapper.getBoundingClientRect().top < 0) {
          resizeAppHeightDebounce();
        }
      }, 1000);
    };

    let resizeAppHeightDebounceTimer;
    const resizeAppHeightDebounce = () => {
      clearTimeout(resizeAppHeightDebounceTimer);
      resizeAppHeightDebounceTimer = setTimeout(() => {
        appHeight();
      }, 1000); // 1s helps account for slow reflows/repaints
    };

    chatIframeWrapper.show = () => {
      this.element.setAttribute("aria-hidden", "false");
      sessionStorage.setItem("showChat", "true");
    };
    chatIframeWrapper.open = () => {
      if (!this.injected) {
        this.injected = this.injectChurchChat();
      }
      if (!this.injected) return;
      chatIframeWrapper.setAttribute("aria-hidden", "false");
      if ((fab && window.innerWidth <= 599) || window.innerHeight <= 599) {
        fab.hide();
      } else {
        fab.show();
        fab.open();
      }
      window.addEventListener("resize", resizeAppHeightDebounce);
      appHeight();
      sessionStorage.setItem("chatOpen", "true");

      // Emit chat opened event for enhanced persistent CTA
      window.postMessage({ type: "chat-opened" }, window.location.origin);
    };
    chatIframeWrapper.close = () => {
      this.moveFocusOutsideChat();
      chatIframeWrapper.setAttribute("aria-hidden", "true");
      fab.show();
      window.removeEventListener("resize", resizeAppHeightDebounce);
      docElem.style.setProperty("--app-height", `100vh`);
      sessionStorage.removeItem("chatOpen");
      fab.close();
      this.sendChatAnalytics("close");

      // Emit chat closed event for enhanced persistent CTA
      window.postMessage({ type: "chat-closed" }, window.location.origin);
    };
    chatIframeWrapper.toggle = () => {
      if (chatIframeWrapper.getAttribute("aria-hidden") === "true") {
        chatIframeWrapper.open();
      } else {
        chatIframeWrapper.close();
      }
    };
    chatIframeWrapper.injectChurchChat = () => {
      this.injectChurchChat();
    };
    remoteComponentSubscribe(element.id, () => {
      chatIframeWrapper.toggle();
    });
    closeButton.addEventListener("mousedown", (event) => {
      event.preventDefault();
    });
    closeButton.addEventListener("click", () => {
      chatIframeWrapper.close();
      fab && fab.close();
    });
    // chatOpen means it should open the chat, meaning it was likely open on the previous page
    if (sessionStorage.getItem("chatOpen")) {
      // if we need to open the chat window because it was open on a previous page load, do it onload to make sure things are available.
      window.addEventListener("load", () => {
        chatIframeWrapper.open();
        fab && fab.open();
      });
    }
    // showChat (var or session) means to visibly put the chat into the page
    if (showChat || sessionStorage.getItem("showChat")) {
      window.addEventListener("load", () => {
        chatIframeWrapper.show();
      });
    }
    this.sendChatAnalytics = (event) => {
      postAnalytics({
        event: window.digitalDataEventsCUC.component.chat,
        component: {
          info: {
            name: "cuc",
            adobeId: window._satellite?.getVar("user_experience_cloud_id"),
            action: event
          },
          category: {
            primary: "chat"
          }
        }
      });
    };
  }

  moveFocusOutsideChat = () => {
    const activeElement = document.activeElement;

    if (!activeElement || !this.chatIframeWrapper?.contains(activeElement)) {
      return;
    }

    const fab = this.element.querySelector(
      "[data-type='floating-action-button']"
    );

    if (fab && typeof fab.focus === "function") {
      fab.focus();
      return;
    }

    if (document.body && typeof document.body.focus === "function") {
      const hadTabIndex = document.body.hasAttribute("tabindex");

      if (!hadTabIndex) {
        document.body.setAttribute("tabindex", "-1");
      }

      document.body.focus();

      if (!hadTabIndex) {
        document.body.removeAttribute("tabindex");
      }

      return;
    }

    if (typeof activeElement.blur === "function") {
      activeElement.blur();
    }
  };

  injectChurchChat = () => {
    if (this.churchChatLoaded || document.querySelector("#churchchat")) {
      return true;
    }
    const searchParams = new URLSearchParams(window.location.search);
    const lang = document
      ?.querySelector('meta[property="omni:language"]')
      ?.getAttribute("content");
    const adobeID = window._satellite?.getVar("user_experience_cloud_id");
    const cid = searchParams.get("cid");

    const chatUrl = parseAllowedChatUrl(window.PUBLIC_ENV?.CHAT_ORIGIN);
    if (!chatUrl) return false;
    chatUrl.searchParams.set("lang", lang || "");
    chatUrl.searchParams.set("embedded", "true");
    chatUrl.searchParams.set("adobeId", adobeID || "");
    chatUrl.searchParams.set("startBot", "true");
    chatUrl.searchParams.set("source", location.origin + location.pathname);
    if (cid) {
      chatUrl.searchParams.set("cid", cid);
    }

    const iframe = document.createElement("iframe");
    iframe.id = "churchchat";
    iframe.title = "Church Chat";
    iframe.setAttribute("allow", "fullscreen");
    iframe.setAttribute("allowfullscreen", "");
    iframe.setAttribute("referrerpolicy", "origin");
    iframe.src = chatUrl.toString();

    this.chatIframeWrapper.prepend(iframe);
    this.iframe = iframe;
    this.iframe.onload = () => {
      if (getIframeOrigin(this.iframe) !== window.location.origin) {
        return;
      }

      const iframeDoc =
        this.iframe.contentDocument || this.iframe.contentWindow?.document;
      const contentWrapper = iframeDoc?.querySelector("[headerheight]");
      if (contentWrapper) {
        contentWrapper.style.minHeight = "0";
      }
    };
    this.sendChatAnalytics("open");
    const chatOrigin = getChatOrigin();
    window.addEventListener(
      "message",
      (event) => {
        if (
          event.origin !== window.location.origin &&
          event.origin !== chatOrigin
        ) {
          return;
        }
        if (
          event.origin === chatOrigin &&
          event.source !== this.iframe.contentWindow
        ) {
          return;
        }
        if (
          event?.data?.action === "sent_first_message" &&
          !sessionStorage.getItem("sentFirstChatTracker")
        ) {
          this.sendChatAnalytics("start");
          sessionStorage.setItem("sentFirstChatTracker", "true");
        }
        if (event?.data?.action === "left_message") {
          const parentWindow =
            window.parent === window ? window : window.parent;

          try {
            const parentWrapper = parentWindow.document.querySelector(
              "[chatIframeWrapper]"
            );
            const parentFab = parentWindow.document.querySelector(
              "[data-type='floating-action-button']"
            );

            parentWrapper?.setAttribute("aria-hidden", "true");
            parentFab?.show?.();
            parentFab?.close?.();
          } catch (error) {
            // Parent access may also be cross-origin when embedded elsewhere.
          }
        }
      },
      false
    );
    return true;
  };
}

export const init = () => {
  initComponent("chat", (element) => new Chat(element));
};
init();
